"use client"

import { useState, type ChangeEvent } from "react"
import { addPromoCodeToCurrentBasketAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { CheckCircleIcon } from "lucide-react"
import { toast } from "sonner"

import { Button, Input } from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import type { UbBasketPromoCodeStoryblok } from "@/components/storyblok/component-types"

interface BasketPromoCodeProps {
  blok: UbBasketPromoCodeStoryblok
}

export default function BasketPromoCodeClient(props: BasketPromoCodeProps) {
  const [promoCode, setPromoCode] = useState<string>("")
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const { basketData, basketLineItems, setBasketData, setIsLoading } = useUbStorefrontState()

  function addPromoCode(promoCode: string) {
    if (promoCode) {
      setIsLoading(true)

      addPromoCodeToCurrentBasketAction(promoCode).then((result) => {
        setBasketData(result.basketData)
        if (result.result === "NOK") {
          //Explicitly override error message! See: https://udobaer.atlassian.net/browse/UBW-765
          setErrorMessage(props.blok.text_wrong_promo_code)
        } else {
          toast.success(props.blok?.text_success ?? "Gutschein wurde erfolgreich hinzugefügt", {
            icon: <CheckCircleIcon className={"size-4 text-green-500"} />,
            description: "",
          })

          setPromoCode("")
        }
        setIsLoading(false)
      })
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setPromoCode(value ? value.trim() : value) //https://udobaer.atlassian.net/browse/UBW-761 Always trim
  }

  const handleSubmit = (event: any) => {
    event.preventDefault()
    setErrorMessage(undefined)
    if (promoCode && promoCode !== "") {
      addPromoCode(promoCode)
    }
  }

  return (
    <>
      {basketData &&
        (basketData?.data?.totalProductQuantity ?? 0) > 0 &&
        basketLineItems &&
        basketLineItems.length > 0 && (
          <div className={"min-h-24 w-full"}>
            <>
              <div className={"flex flex-col gap-2"}>
                <h2
                  className={cn("text-sm font-bold sm:text-sm", {
                    "text-sm": props.blok.compact,
                  })}
                >
                  {props.blok?.label_promo_code ?? "Rabattcode"}
                </h2>
                <form className={cn("grid grid-cols-12 items-center gap-2")} onSubmit={handleSubmit}>
                  <Input
                    className={cn("col-span-12 text-sm sm:col-span-6 sm:text-sm md:col-span-8", {
                      "text-sm sm:text-sm": props.blok.compact,
                    })}
                    placeholder={props.blok?.text_input_placeholder}
                    type="text"
                    name="productId"
                    value={promoCode}
                    onChange={handleChange}
                  />

                  <Button
                    className={"col-span-12 sm:col-span-6 md:col-span-4"}
                    size={"productCard"}
                    variant={props.blok.compact ? "outline_secondary" : "default"}
                    onClick={handleSubmit}
                  >
                    {props.blok?.label_add_promo_code ?? "Rabattcode einlösen"}{" "}
                  </Button>
                </form>
                {errorMessage && <p className={"text-xs text-red-500"}>{errorMessage}</p>}
              </div>
            </>
          </div>
        )}
    </>
  )
}
